import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, getBearerToken } from '../../functions';
import { useAuth } from '../pages/auth/AuthProvider';
import UmrahAdditionalDiscount from './UmrahAdditionalDiscount';

const UmrahConfirmBooking = ({ show, setShow, selectedBooking }) => {
  const handleClose = () => setShow(false);
  const [loading, setLoading] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(selectedBooking);
  const { token } = useAuth();

  // Update currentBooking when selectedBooking prop changes
  useEffect(() => {
    setCurrentBooking(selectedBooking);
  }, [selectedBooking]);

  const handleConfirm = async () => {
    setLoading(true);
    const tid = toast.loading('Confirming booking...');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/umrah-booking/confirm`, {
        booking_id: currentBooking?.id
      }, {
        headers: {
          'Authorization': `Bearer ${getBearerToken()}`
        }
      });

      if (response.data.status) {
        toast.success('Umrah booking confirmed successfully');
        handleClose();
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error(response.data.message || 'Failed to confirm booking');
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
      toast.error(error.response?.data?.message || 'Failed to confirm booking');
    } finally {
      setLoading(false);
      toast.dismiss(tid);
    }
  };

  const handleDiscountSuccess = async () => {
    // Fetch updated booking data after discount is applied
    try {
      const response = await axios.get(`${API_BASE_URL}/umrah-bookings/${currentBooking?.id}`, {
        headers: {
          'Authorization': `Bearer ${getBearerToken()}`
        }
      });
      
      if (response.data && response.data.data) {
        setCurrentBooking(response.data.data);
        setShowDiscount(false);
      }
    } catch (error) {
      console.error('Error fetching updated booking:', error);
      toast.error('Failed to refresh booking data');
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Umrah Booking</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Booking ID:</strong> {currentBooking?.id}
        </div>
        <div className="mb-3">
          <strong>Package:</strong> {currentBooking?.umrah_package?.title}
        </div>
        <div className="mb-3">
          <strong>Room Type:</strong> {currentBooking?.room_type}
        </div>
        <div className="mb-3">
          <strong>Passengers:</strong> {currentBooking?.number_of_passengers ?? currentBooking?.passengers?.length ?? 0}
        </div>
        <div className="mb-3">
          <strong>Subtotal:</strong> PKR {Number(currentBooking?.total_amount ?? 0).toLocaleString()}
        </div>
        {currentBooking?.package_discount_amount > 0 && (
          <div className="mb-3">
            <strong>Package Discount:</strong> -PKR {Number(currentBooking?.package_discount_amount ?? 0).toLocaleString()}
          </div>
        )}
        {currentBooking?.additional_discount_amount > 0 && (
          <div className="mb-3">
            <strong>Additional Discount:</strong> -PKR {Number(currentBooking?.additional_discount_amount ?? 0).toLocaleString()}
            {currentBooking?.additional_discount_name && (
              <span className="text-muted"> ({currentBooking.additional_discount_name})</span>
            )}
          </div>
        )}
        <div className="mb-3">
          <strong>Final Amount:</strong> PKR {Number(currentBooking?.final_amount ?? currentBooking?.total_amount ?? 0).toLocaleString()}
        </div>
        <p className="text-muted">
          Are you sure you want to confirm this Umrah booking? This action cannot be undone.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {token.role === 'admin' && (
              <Button 
                variant="outline-success" 
                onClick={() => setShowDiscount(true)} 
                disabled={loading}
              >
                {currentBooking?.additional_discount_amount > 0 ? 'Edit Discount' : 'Add Discount'}
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} disabled={loading} className="me-2">
              Cancel
            </Button>
            <Button variant="success" onClick={handleConfirm} disabled={loading}>
              {loading ? 'Confirming...' : 'Confirm Booking'}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {/* Discount Modal */}
      {token.role === 'admin' && (
        <UmrahAdditionalDiscount
          show={showDiscount}
          setShow={setShowDiscount}
          selectedBooking={currentBooking}
          onSuccess={handleDiscountSuccess}
        />
      )}
    </Modal>
  );
};

export default UmrahConfirmBooking;